import { RhButton, RhDatePicker, RhSelect } from "@rhythm-ui/react";
import { useQuery } from "@tanstack/react-query";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import { download, generateCsv } from "export-to-csv";
import React, { memo, useMemo, useState } from "react";

import { ClientReportRow } from "@/types/job";

import api from "@/services/api";

import {
  ANALYTICS_INTERVAL_OPTIONS,
  COLUMN_HEADER_MAP,
  COLUMN_ORDER,
  extractDynamicColumns,
  formatCellValue,
  formatColumnHeader,
  formatRange,
  formatWeek,
  getCurrentMonthDateRange,
  getPeriodSortingValue,
} from "./utils/analyticsUtils";

const ClientReportAnalyticsTable = ({ jobId }: { jobId?: number }) => {
  const [selectedInterval, setSelectedInterval] = useState(
    ANALYTICS_INTERVAL_OPTIONS[1],
  );

  const [dateRange, setDateRange] = useState(getCurrentMonthDateRange());

  const startDate = dateRange?.[0]
    ? dayjs(dateRange[0]).format("YYYY-MM-DD")
    : dayjs().startOf("month").format("YYYY-MM-DD");

  const endDate = dateRange?.[1]
    ? dayjs(dateRange[1]).format("YYYY-MM-DD")
    : dayjs().format("YYYY-MM-DD");

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "JOB_ANALYTICS_CLIENT_REPORT",
      jobId,
      startDate,
      endDate,
      selectedInterval.value,
    ],
    queryFn: () =>
      api.company
        .getClientReportAnalytics(
          jobId!,
          startDate!,
          endDate!,
          selectedInterval.value,
        )
        .then((res) => res.data),
    enabled: Boolean(jobId && startDate && endDate),
  });

  const isPageLoading = isLoading || isFetching;

  const clientReport = useMemo(() => {
    if (!data) return [];

    return selectedInterval.value === "range"
      ? (data.range ?? [])
      : (data.weekly ?? []);
  }, [data, selectedInterval.value]);

  const allColumns = useMemo(() => {
    const extracted = extractDynamicColumns(clientReport);

    return COLUMN_ORDER.filter((col) => extracted.includes(col));
  }, [clientReport]);

  const columns = useMemo<ColumnDef<ClientReportRow>[]>(() => {
    const periodColumn: ColumnDef<ClientReportRow> = {
      header: selectedInterval.value === "range" ? "Range" : "Week",
      id: selectedInterval.value === "range" ? "range" : "week",
      accessorFn: (row) => getPeriodSortingValue(row, selectedInterval.value),
      cell: ({ row }) => {
        if (selectedInterval.value === "range") {
          return row.original.range ? formatRange(row.original.range) : "-";
        }
        return row.original.week ? formatWeek(row.original.week) : "-";
      },
      enableSorting: true,
    };

    const dynamicColumns: ColumnDef<ClientReportRow>[] = allColumns.map(
      (col) => ({
        id: col,
        header: COLUMN_HEADER_MAP[col] ?? formatColumnHeader(col),
        accessorKey: col,
        cell: ({ row }) => formatCellValue(row.original[col]),
        enableSorting: false,
      }),
    );

    return [periodColumn, ...dynamicColumns];
  }, [allColumns, selectedInterval.value]);

  const table = useReactTable({
    data: clientReport,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleExportRows = () => {
    if (!table) return;

    const rows = table.getRowModel().rows;

    if (!rows.length) return;

    const rowData = rows.map((row) => {
      const exportRow: Record<string, any> = {};

      if (selectedInterval.value === "range") {
        exportRow["Range"] = row.original.range
          ? formatRange(row.original.range)
          : "-";
      } else {
        exportRow["Week"] = row.original.week
          ? formatWeek(row.original.week)
          : "-";
      }

      allColumns.forEach((col) => {
        const header = COLUMN_HEADER_MAP[col] ?? formatColumnHeader(col);

        exportRow[header] = formatCellValue(row.original[col]);
      });

      return exportRow;
    });

    const filename = `Client_Report_${startDate}_to_${endDate}`;

    const csv = generateCsv({
      filename,
      useKeysAsHeaders: true,
    })(rowData);

    download({
      filename,
    })(csv);
  };

  return (
    <div className="w-full space-y-6 p-6">
      <div className="flex w-full items-center justify-between">
        <h4>Client Report Analytics</h4>

        <div className="z-50 flex items-center gap-4">
          <RhSelect
            instanceId="client-report-interval"
            options={ANALYTICS_INTERVAL_OPTIONS}
            value={selectedInterval}
            onChange={(val: any) => setSelectedInterval(val)}
            isSearchable={false}
            className="min-w-[120px]"
            isDisabled={isPageLoading}
          />

          <RhDatePicker
            options={{ mode: "range" }}
            className="w-full max-w-[230px]"
            value={dateRange}
            onChange={(value: string[]) => setDateRange(value)}
            disabled={isPageLoading}
          />

          <RhButton
            variant="white"
            onClick={handleExportRows}
            disabled={isPageLoading || clientReport.length === 0}
          >
            Export CSV
          </RhButton>
        </div>
      </div>

      {isPageLoading ? (
        <LoadingState />
      ) : clientReport.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="rounded bg-slate-100 p-5">
          <div className="w-full overflow-x-auto">
            <div className="min-w-[1000px]">
              <DataTable table={table} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientReportAnalyticsTable;

const LoadingState = memo(() => (
  <div className="flex h-40 items-center justify-center">
    <p className="text-gray-500">Loading client report data...</p>
  </div>
));

const EmptyState = memo(() => (
  <div className="flex h-40 items-center justify-center rounded border bg-slate-50">
    <p className="text-gray-500">No data available</p>
  </div>
));

const DataTable = memo(({ table }: { table: any }) => (
  <div className="max-h-[420px] overflow-y-auto rounded border bg-white">
    <table className="min-w-full border-collapse">
      <thead className="bg-slate-100">
        {table.getHeaderGroups().map((headerGroup: any) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header: any) => {
              const isPeriodColumn =
                header.column.id === "range" || header.column.id === "week";

              return (
                <th
                  key={header.id}
                  className={`sticky top-0 min-w-[140px] border bg-slate-100 px-3 py-2 text-left text-xs font-semibold text-gray-700 ${
                    isPeriodColumn
                      ? "left-0 z-30 shadow-[2px_0_4px_rgba(0,0,0,0.08)]"
                      : "z-20"
                  }`}
                >
                  <div
                    className={`flex items-center gap-1 ${
                      header.column.getCanSort()
                        ? "cursor-pointer select-none"
                        : ""
                    }`}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        ))}
      </thead>

      <tbody>
        {table.getRowModel().rows.map((row: any) => (
          <tr key={row.id} className="hover:bg-slate-50">
            {row.getVisibleCells().map((cell: any) => {
              const isPeriodColumn =
                cell.column.id === "range" || cell.column.id === "week";

              return (
                <td
                  key={cell.id}
                  className={`border px-3 py-2 text-sm text-gray-800 ${
                    isPeriodColumn
                      ? "sticky left-0 z-10 bg-white shadow-[2px_0_4px_rgba(0,0,0,0.08)]"
                      : ""
                  }`}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
));
